import React, { Suspense, useEffect, useRef, useState } from "react";

import { ModalVariant } from "@at/design-system-patterns";
import { dataAttrTestId } from "@at/formatter-utils";

import { Channel } from "@at/sauron-platform-core";
import { GalleryImage102 } from "@at/track-core";
import loadable from "@loadable/component";

import * as Styled from "./gallery-modal.styled";
import { StyledModal } from "./gallery-modal.styled";
import { GalleryModalProps } from "./gallery-modal.types";
import { useChannel } from "../../../utils/hooks/use-channel";
import {
  comScorePageViewTrack,
  getGalleryImageContext,
  ImageGalleryState,
} from "../../../utils/tracking/tracking-data-utils";
import { useTracking } from "../../../utils/tracking/use-tracking";
import AdvertShare from "../../advert/advert-image/actions/action-share";
import GalleryBackButton from "../gallery-back-button/gallery-back-button";
import { GalleryCategoryPillsToolbar } from "../gallery-category-pills-toolbar/gallery-category-pill-toolbar";
import { GalleryMediaTabs } from "../gallery-media-tabs/gallery-media-tabs";
import { GalleryIFrameWrapper } from "../gallery-media-tabs/gallery-media-tabs.styled";
import { GalleryModalGrid } from "../gallery-modal-grid/gallery-modal-grid";
import { GalleryModalSlider } from "../gallery-modal-slider/gallery-modal-slider";
import {
  GalleryMediaTypes,
  getGalleryAssets,
  groupByCategory,
} from "../gallery.utils";
import { IframeWrapper } from "../iframe-wrapper-grid";

const LazyAdvertSaveIcon = loadable(() => {
  return import("../../advert/advert-image/actions/action-save-button");
});

export const GalleryModal = ({
  initialGridSlide,
  images,
  videoUrl,
  spinUrl,
  isLeasingPDPPage,
  setShowGalleryModal,
}: GalleryModalProps) => {
  const channel = useChannel();

  const [slide, setSlide] = useState(initialGridSlide);
  const [showSliderModal, setShowSliderModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const { trackEvent, trackModalEvent, trackComponentViewedEvent } =
    useTracking();

  const hasSpin = !!spinUrl;
  const hasVideo = !!videoUrl;
  const [selectedMediaType, setSelectedMediaType] = useState(
    images.length === 0
      ? hasVideo
        ? GalleryMediaTypes.VIDEO
        : GalleryMediaTypes.SPIN
      : GalleryMediaTypes.IMAGE,
  );

  const trackedGridImages = useRef<GalleryImage102[]>([]);
  const trackedSliderImages = useRef<GalleryImage102[]>([]);

  const skipGridView =
    images &&
    (isLeasingPDPPage ? images.length < 4 : images.length < 6) &&
    !hasVideo &&
    !hasSpin;

  images = [
    ...images,
    ...getGalleryAssets({
      images: [],
    }),
  ];

  const groupedImages = groupByCategory(images);
  const categories = Object.keys(groupedImages).sort();
  const selectedChannel =
    channel === Channel.cars ||
    channel === Channel.vans ||
    channel === Channel.bikes;

  const hasMultipleMediaTypes = categories.length > 1 && (hasSpin || hasVideo);

  const returnToProductPage = () => {
    setShowGalleryModal(false);
    trackComponentViewedEvent({
      extraEntities: skipGridView
        ? trackedSliderImages.current
        : trackedGridImages.current,
    });
    skipGridView
      ? (trackedSliderImages.current = [])
      : (trackedGridImages.current = []);

    trackEvent({ trackingLabel: "grid-gallery-close-modal", action: "close" });
  };

  const returnToGalleryModalGrid = () => {
    setShowSliderModal(false);
    trackComponentViewedEvent({ extraEntities: trackedSliderImages.current });
    trackedSliderImages.current = [];
    trackEvent({
      trackingLabel: "fullscreen-gallery-close-modal",
      action: "close",
    });
  };

  const handleImageTracked = (
    imageId: string,
    index: number,
    type: ImageGalleryState,
  ) => {
    const trackedImages =
      type === ImageGalleryState.in_grid
        ? trackedGridImages.current
        : trackedSliderImages.current;

    const isImageTracked = trackedImages.some(
      (trackedImage) => trackedImage.image_id === imageId,
    );

    if (!isImageTracked) {
      trackedImages.push(
        getGalleryImageContext(imageId, type, index, activeCategory),
      );
    }
  };

  const handleKeydown = (evt: KeyboardEvent) => {
    if (evt.code !== "Escape") return;
    showSliderModal ? returnToGalleryModalGrid() : returnToProductPage();
  };

  const handleCategoryChange = (category: string) => {
    setSlide(0);
    setActiveCategory(category);

    trackComponentViewedEvent({ extraEntities: trackedGridImages.current });

    trackedGridImages.current = [];
    trackEvent({ trackingLabel: `filter-pills-${category.toLowerCase()}` });
    comScorePageViewTrack();
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  });

  return (
    <StyledModal
      data-testid="new-gallery-modal"
      isShowing={true}
      hasBackLink={false}
      closeText=" "
      variation={ModalVariant.FULL}
      hideFn={() => {}}
      portal={true}
      overlayName="fullscreen-gallery"
      trackingCallback={({ modalAction }) => {
        trackModalEvent({ trackingLabel: "fullscreen-gallery", modalAction });
      }}
      shouldTrackOnOpen={false}
      disableEscButton={true}
    >
      {skipGridView || showSliderModal ? (
        <Styled.SliderModal>
          <GalleryModalSlider
            {...{
              images,
              skipGridView,
              slide,
              isLeasingPDPPage,
              hasVideo,
              hasSpin,
              activeCategory,
              returnToGalleryModalGrid,
              returnToProductPage,
              handleImageTracked,
            }}
          ></GalleryModalSlider>
        </Styled.SliderModal>
      ) : (
        <Styled.GridModal>
          <Styled.GalleryIconBar hasMultipleMediaTypes={hasMultipleMediaTypes}>
            <GalleryBackButton closeFn={returnToProductPage} />
            <GalleryMediaTabs
              {...{
                selectedMediaType,
                setSelectedMediaType,
                images,
                hasSpin,
                hasVideo,
              }}
            ></GalleryMediaTabs>
            <Styled.SaveShareContainer className="gallery-save-share">
              {!isLeasingPDPPage && (
                <Suspense fallback={""}>
                  <Styled.Save>
                    <LazyAdvertSaveIcon />
                  </Styled.Save>
                </Suspense>
              )}
              <AdvertShare />
            </Styled.SaveShareContainer>
          </Styled.GalleryIconBar>

          {!isLeasingPDPPage &&
            categories.length > 1 &&
            selectedChannel &&
            selectedMediaType === GalleryMediaTypes.IMAGE && (
              <GalleryCategoryPillsToolbar
                categories={categories}
                activeCategory={activeCategory}
                hasMultipleMediaTypes={hasMultipleMediaTypes}
                handleCategoryChange={handleCategoryChange}
              />
            )}

          {selectedMediaType === GalleryMediaTypes.IMAGE && (
            <GalleryModalGrid
              {...{
                images,
                slide,
                onImageClick: (index) => {
                  setShowSliderModal(true);
                  setSlide(index);
                  comScorePageViewTrack();
                },
                activeCategory,
                handleImageTracked,
              }}
            />
          )}

          {selectedMediaType === GalleryMediaTypes.VIDEO && hasVideo && (
            <GalleryIFrameWrapper {...{ [dataAttrTestId]: "video-component" }}>
              <IframeWrapper media={videoUrl} iframeType="VIDEO" />
            </GalleryIFrameWrapper>
          )}

          {selectedMediaType === GalleryMediaTypes.SPIN && hasSpin && (
            <GalleryIFrameWrapper {...{ [dataAttrTestId]: "spin-component" }}>
              <IframeWrapper media={spinUrl} iframeType="SPIN" />
            </GalleryIFrameWrapper>
          )}
        </Styled.GridModal>
      )}
    </StyledModal>
  );
};
